import { useSearchParams } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mapSearchParamsToSearchRequest,
  QUERY_SEARCH_PARAM_ACTION,
  QUERY_SEARCH_PARAM_ALLERGEN,
  QUERY_SEARCH_PARAM_BRANDS,
  QUERY_SEARCH_PARAM_CA,
  QUERY_SEARCH_PARAM_CATALOG_AGRID,
  QUERY_SEARCH_PARAM_CATALOG_HAGRID,
  QUERY_SEARCH_PARAM_CATALOG_HWGID,
  QUERY_SEARCH_PARAM_ECOSCORE,
  QUERY_SEARCH_PARAM_HERGESTELLT,
  QUERY_SEARCH_PARAM_HWGID,
  QUERY_SEARCH_PARAM_LABELS,
  QUERY_SEARCH_PARAM_NOVELTIES,
  QUERY_SEARCH_PARAM_PAGE,
  QUERY_SEARCH_PARAM_ROHSTOFFHERKUNFT,
  QUERY_SEARCH_PARAM_SEARCHTERM,
  QUERY_SEARCH_PARAM_SPECIAL_ASSORTMENT,
  QUERY_SEARCH_PARAM_SPECIALDIET,
  QUERY_SEARCH_PARAM_SUPPLIERGROUPNAMES,
  QUERY_SEARCH_PARAM_TK
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import type { ISearchRequestPagingDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';

function setOrDeleteParam(params: URLSearchParams, key: string, value: string | undefined) {
  if (value !== undefined) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

function setOrDeleteListParam(params: URLSearchParams, key: string, values: number[] | undefined) {
  if (values && values.length > 0) {
    params.set(key, values.join(','));
  } else {
    params.delete(key);
  }
}

function setOrDeleteBooleanParam(params: URLSearchParams, key: string, value: boolean | undefined) {
  setOrDeleteParam(params, key, value !== undefined ? value.toString() : undefined);
}

export function useArticleSearchQueryParams() {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [urlSearchRequest, setUrlSearchRequest] = useState<ISearchRequestPagingDTOData>(() =>
    mapSearchParamsToSearchRequest(searchParams)
  );

  useEffect(() => {
    const searchRequest = mapSearchParamsToSearchRequest(searchParams);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrlSearchRequest(searchRequest);
  }, [i18n.language, searchParams]);

  const setSearchQueryParam = useCallback(
    (searchRequest: ISearchRequestPagingDTOData) => {
      setSearchParams(prev => {
        prev.set(QUERY_SEARCH_PARAM_PAGE, searchRequest?.page ? searchRequest.page.toString() : '0');

        // Mit Suchbegriff werden die hwgIds als Filter-Liste geführt, ohne Suchbegriff als Katalog-Navigation
        setOrDeleteListParam(
          prev,
          QUERY_SEARCH_PARAM_HWGID,
          searchRequest.searchTerm ? searchRequest.hwgIds : undefined
        );
        setOrDeleteParam(
          prev,
          QUERY_SEARCH_PARAM_CATALOG_HWGID,
          !searchRequest.searchTerm && searchRequest.hwgIds?.length ? searchRequest.hwgIds[0].toString() : undefined
        );
        setOrDeleteParam(
          prev,
          QUERY_SEARCH_PARAM_CATALOG_HAGRID,
          searchRequest.hagrIds ? searchRequest.hagrIds[0].toString() : undefined
        );
        setOrDeleteParam(
          prev,
          QUERY_SEARCH_PARAM_CATALOG_AGRID,
          searchRequest.agrIds ? searchRequest.agrIds[0].toString() : undefined
        );

        setOrDeleteParam(prev, QUERY_SEARCH_PARAM_SEARCHTERM, searchRequest.searchTerm || undefined);

        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_BRANDS, searchRequest.brands);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_ROHSTOFFHERKUNFT, searchRequest.rohstoffHerkunft);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_HERGESTELLT, searchRequest.hergestellt);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_SPECIALDIET, searchRequest.specialDiet);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_ECOSCORE, searchRequest.ecoScore);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_LABELS, searchRequest.labels);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_SUPPLIERGROUPNAMES, searchRequest.supplierGroups);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_SPECIAL_ASSORTMENT, searchRequest.specialAssortments);
        setOrDeleteListParam(prev, QUERY_SEARCH_PARAM_ALLERGEN, searchRequest.allergene);

        setOrDeleteBooleanParam(prev, QUERY_SEARCH_PARAM_TK, searchRequest.tk);
        setOrDeleteBooleanParam(prev, QUERY_SEARCH_PARAM_ACTION, searchRequest.action);
        setOrDeleteBooleanParam(prev, QUERY_SEARCH_PARAM_NOVELTIES, searchRequest.novelties);
        setOrDeleteBooleanParam(prev, QUERY_SEARCH_PARAM_CA, searchRequest.searchInCoreAssortment);

        // Remove emtpy values from URLSearchParams -> otherwise the URL will be polluted with empty values
        for (const [key, value] of prev.entries()) {
          if (value === '') {
            prev.delete(key);
          }
        }

        return prev;
      });
    },
    [setSearchParams]
  );

  return { urlSearchRequest, setSearchQueryParam };
}
